import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthFrame from "./AuthFrame";
import { TitleBlock, Field, Input, Note, ErrorNote, Actions, PrimaryBtn, SecondaryBtn } from "./pieces";
import { useIsPhone } from "../../ForBrands/useIsPhone";
import { sendSignupCode } from "./authV2Services";
import { formatPhone, usPhoneDigits } from "./phoneFormat";
import { readSignup, writeSignup } from "./signupState";
import { routeAfterVerify, codeChannelFor } from "./signupFlow";
import { accountTypeOf } from "./accountTypes";

// 02 · Email and phone, the same for every account type. The code goes by
// text when a mobile number is given (by email when Twilio is off or
// refuses, which the next screen says), otherwise by email.
const codeLine = (phone) => (phone
  ? "We will text a 6-digit code to your mobile."
  : "We will email you a 6-digit code. Add a mobile number to get it by text.");

const SignupEmailPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Read once, when the page opens, so the forward below only acts on the
  // state the page started with.
  const [saved] = useState(readSignup);
  const type = accountTypeOf(saved.accountType);
  const [email, setEmail] = useState(saved.email || params.get("email") || "");
  const [phone, setPhone] = useState(usPhoneDigits(saved.phone));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // This page renders AuthFrame itself, so it sits outside the phone theme
  // and reads the viewport directly.
  const onPhone = useIsPhone();
  // Phones offer the way back to the social sign-ins under Send code (Figma
  // 1576:694, decision D-o6). Only athletes: the coach frame (2466:18811)
  // leaves it out, and the other types' Welcome has Google alone.
  const offerSocial = onPhone && type.key === "IS_ATHLETE";

  useEffect(() => { document.title = "Create your account · CACHE"; }, []);
  // Already confirmed a code in this tab: carry on from where that leads.
  useEffect(() => {
    if (saved.ticket) navigate(routeAfterVerify(saved), { replace: true });
  }, [saved, navigate]);

  const valid = /\S+@\S+\.\S+/.test(email.trim());
  const send = async () => {
    const em = email.trim().toLowerCase();
    if (!valid) { setError("Enter a valid email."); return; }
    if (phone && phone.length !== 10) { setError("Enter a 10-digit mobile number, or leave it blank."); return; }
    setBusy(true); setError("");
    try {
      const { ok, status, data } = await sendSignupCode({ email: em, phone, channel: codeChannelFor(phone) });
      if (status === 409) { setError("An account already uses this email."); return; }
      if (!ok) { setError(data?.message || "We could not send the code. Try again."); return; }
      writeSignup({ email: em, phone, channel: data.channel, sentTo: data.to, resendInSeconds: data.resendInSeconds || 30, fallback: data.fallback || null });
      navigate("/signup/verify");
    } catch {
      setError("We could not reach CACHE. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <AuthFrame title="Create your account" onBack={() => navigate("/welcome")}>
      <form className="contents" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <TitleBlock eyebrow="Email path" title="Use email and phone" sub={[codeLine(phone), type.emailLine].filter(Boolean).join(" ")} />
        <Field label="Email">
          <Input type="email" autoComplete="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Mobile number">
          <Input inputMode="tel" autoComplete="tel" placeholder="(123)-456-7890" value={formatPhone(phone)} onChange={(e) => setPhone(usPhoneDigits(e.target.value))} />
        </Field>
        {type.emailNote && <Note>{type.emailNote}</Note>}
        {error && <ErrorNote error={error} onLogin={() => navigate("/login")} />}
        <Actions>
          <PrimaryBtn type="submit" loading={busy} disabled={!valid}>Send code</PrimaryBtn>
          {offerSocial && <SecondaryBtn onClick={() => navigate("/welcome")}>Use a social account instead</SecondaryBtn>}
        </Actions>
      </form>
    </AuthFrame>
  );
};

export default SignupEmailPage;
